// Handoff Scan — admin panel embedded in the member profile's offboarding
// section (user_detail.jsx). Launches a scan of the member's synced
// email/meeting notes and renders the classified findings table: what has
// live momentum and needs a named new owner, what's already at risk, and
// which referral relationships need a warm handoff.
//
// Runs execute on the agent worker — we poll the runs list while one is
// queued/running (same interval pattern as lost_deals.jsx). All endpoints
// are admin-only (view_team_activity), same gate as the offboarding report
// this panel sits under.

const HS_HUES = {
  live_momentum: 'green',
  at_risk: 'rose',
  referral_channel: 'violet',
  none: 'slate',
  unclassified: 'slate',
};

const HS_LABELS = {
  live_momentum: 'Live momentum',
  at_risk: 'At risk',
  referral_channel: 'Referral channel',
  none: 'Routine',
  unclassified: 'Unclassified',
};

const HS_ACTIONABLE = ['live_momentum', 'at_risk', 'referral_channel'];

const HandoffScanPanel = ({ userId, memberName, goToCompany }) => {
  const { useState, useEffect, useCallback } = React;
  const [runs, setRuns] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [results, setResults] = useState(null);
  const [days, setDays] = useState(90);
  const [starting, setStarting] = useState(false);
  const [filter, setFilter] = useState('actionable'); // actionable | <classification> | all

  const loadRuns = useCallback(async () => {
    try {
      const rows = await window.AtlasApi.handoffScanRuns(userId);
      setRuns(rows || []);
      return rows || [];
    } catch (_) { setRuns([]); return []; }
  }, [userId]);

  useEffect(() => { setRuns(null); setSelectedId(null); setResults(null); loadRuns(); }, [loadRuns]);

  // Default selection: newest completed run.
  useEffect(() => {
    if (selectedId || !runs || !runs.length) return;
    const done = runs.find((r) => r.status === 'completed');
    if (done) setSelectedId(done.id);
  }, [runs, selectedId]);

  // Poll while a run is in flight; select the fresh results when it lands. A
  // scan takes minutes, so the ladder backs off from 2.5s as it ages and the
  // hook sits out any stretch where the tab is hidden.
  const inFlight = (runs || []).some((r) => r.status === 'queued' || r.status === 'running');
  window.usePolling(
    useCallback(async () => {
      const rows = await loadRuns();
      const stillGoing = rows.some((r) => r.status === 'queued' || r.status === 'running');
      if (!stillGoing) {
        const newest = rows.find((r) => r.status === 'completed');
        if (newest) setSelectedId(newest.id);
        const failed = rows.find((r) => r.status === 'failed');
        if (!newest && failed) {
          pushToast({ type: 'error', title: 'Handoff scan failed', body: failed.output_summary || '' });
        }
      }
      return rows;
    }, [loadRuns]),
    {
      active: inFlight,
      first: 2500,
      // Statuses only — run rows carry timestamps that churn without the scan
      // having actually moved on.
      signature: (rows) => (rows || []).map((r) => `${r.id}:${r.status}`).join(','),
    },
  );

  useEffect(() => {
    if (!selectedId) { setResults(null); return; }
    let dead = false;
    setResults(null);
    window.AtlasApi.handoffScanResults(selectedId)
      .then((res) => { if (!dead) setResults(res); })
      .catch(() => { if (!dead) setResults({ columns: [], rows: [], row_count: 0, status: 'failed' }); });
    return () => { dead = true; };
  }, [selectedId]);

  const startRun = async () => {
    setStarting(true);
    try {
      await window.AtlasApi.createHandoffScan(userId, days);
      pushToast({ type: 'info', title: 'Handoff scan started', body: `Scanning ${memberName}'s last ${days} days of synced activity.` });
      loadRuns();
    } catch (e) {
      let msg = (e && e.message) || 'Unknown error';
      try { const m = msg.match(/\{.*\}$/s); const p = m && JSON.parse(m[0]); if (p && p.detail) msg = p.detail; } catch (_) { /* keep raw */ }
      pushToast({ type: 'error', title: 'Could not start the scan', body: msg });
    } finally {
      setStarting(false);
    }
  };

  const rows = (results && results.rows) || [];
  const filtered = filter === 'all' ? rows
    : filter === 'actionable' ? rows.filter((r) => HS_ACTIONABLE.includes(r.classification))
    : rows.filter((r) => r.classification === filter);
  const counts = {};
  rows.forEach((r) => { counts[r.classification] = (counts[r.classification] || 0) + 1; });
  const selectedRun = (runs || []).find((r) => r.id === selectedId);

  const exportRows = () => {
    const keys = (results && results.columns) || [];
    window.exportCsv('handoff_scan.csv', keys.map((c) => ({ key: c, label: c })), filtered);
  };

  const cellHead = { padding: '6px 10px', fontSize: 10.5, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', textAlign: 'left', whiteSpace: 'nowrap', position: 'sticky', top: 0, background: 'var(--surface)', borderBottom: '1px solid var(--border)', zIndex: 1 };
  const cell = { padding: '7px 10px', fontSize: 12, color: 'var(--text-2)', verticalAlign: 'top', borderBottom: '1px solid var(--border)' };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Launch row */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 11, fontWeight: 600, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text-4)' }}>Handoff scan</span>
        <span style={{ flex: 1 }} />
        <window.Picker value={days} onChange={(v) => setDays(Number(v))} size="sm" placeholder="Window"
          options={[{ value: 30, label: 'Last 30 days' }, { value: 90, label: 'Last 90 days' }, { value: 180, label: 'Last 180 days' }, { value: 365, label: 'Last 12 months' }]} />
        <window.Btn size="sm" variant="accent" disabled={starting || inFlight} onClick={startRun}>
          {inFlight ? 'Scanning…' : 'Run scan'}
        </window.Btn>
      </div>

      {/* Body */}
      {!runs ? (
        <window.SkeletonRows rows={2} />
      ) : !runs.length ? (
        <div style={{ border: '1px dashed var(--border-strong)', borderRadius: 8, padding: '14px 16px', fontSize: 12, color: 'var(--text-3)' }}>
          No scans yet.
        </div>
      ) : (
        <>
          {runs.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <window.Picker value={selectedId || ''} onChange={(v) => setSelectedId(v || null)} size="sm" placeholder="Scan" style={{ maxWidth: 340 }} menuMinWidth={300}
                options={runs.map((r) => ({
                  value: String(r.id), disabled: r.status !== 'completed',
                  label: `${window.fmtDate ? window.fmtDate(r.created_at) : r.created_at} · ${(r.options && r.options.days) || '—'}d`,
                  hint: [r.status, r.row_count != null ? `${r.row_count} companies` : null].filter(Boolean).join(' · '),
                }))} />
              {results && rows.length > 0 && (
                <>
                  <span style={{ display: 'inline-flex', border: '1px solid var(--border)', borderRadius: 6, overflow: 'hidden' }}>
                    {[['actionable', `Actionable ${HS_ACTIONABLE.reduce((s, k) => s + (counts[k] || 0), 0)}`],
                      ...HS_ACTIONABLE.map((k) => [k, `${HS_LABELS[k]} ${counts[k] || 0}`]),
                      ['all', `All ${rows.length}`]].map(([k, label], i) => (
                      <button key={k} onClick={() => setFilter(k)} style={{
                        height: 22, padding: '0 8px', fontSize: 11, cursor: 'pointer',
                        border: 'none', borderLeft: i === 0 ? 'none' : '1px solid var(--border)',
                        background: filter === k ? 'var(--surface-hover)' : 'var(--surface)',
                        color: filter === k ? 'var(--text)' : 'var(--text-3)',
                        fontWeight: filter === k ? 600 : 500,
                      }}>{label}</button>
                    ))}
                  </span>
                  <window.Btn size="xs" variant="outline" onClick={exportRows}>CSV</window.Btn>
                </>
              )}
            </div>
          )}

          {selectedRun && selectedRun.output_summary && (
            <div style={{ fontSize: 12, color: 'var(--text-2)' }}>{selectedRun.output_summary}</div>
          )}

          {inFlight && !results && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Scan in progress</div>
          )}

          {selectedId && !results && !inFlight && <window.SkeletonRows rows={3} />}

          {results && !filtered.length && rows.length > 0 && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>Nothing in this bucket.</div>
          )}
          {results && !rows.length && results.status === 'completed' && (
            <div style={{ fontSize: 12, color: 'var(--text-3)' }}>No synced activity found in this window.</div>
          )}

          {filtered.length > 0 && (
            <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, overflow: 'auto', maxHeight: 480 }}>
              <table style={{ borderCollapse: 'collapse', width: '100%', minWidth: 920 }}>
                <thead>
                  <tr>
                    {['Classification', 'Company', 'Key contact', 'Where it stands', 'Suggested pick-up', 'Last activity', 'Activity', 'Evidence'].map((h) => (
                      <th key={h} style={cellHead}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((r, i) => (
                    <tr key={i}>
                      <td style={cell}>
                        <window.Pill hue={HS_HUES[r.classification] || 'slate'} sz="sm">{HS_LABELS[r.classification] || r.classification}</window.Pill>
                        {r.confidence && <div style={{ fontSize: 10, color: 'var(--text-4)', marginTop: 2 }}>{r.confidence}</div>}
                        {r.unanswered_inbound && <div style={{ fontSize: 10, color: 'var(--rose-fg)', marginTop: 2 }}>unanswered</div>}
                      </td>
                      <td style={{ ...cell, fontWeight: 600, color: 'var(--text)', maxWidth: 180 }}>
                        {r.company_id && goToCompany ? (
                          <button onClick={() => goToCompany(r.company_id)}
                            style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, padding: 0, textAlign: 'left' }}>
                            {r.company || '—'}
                          </button>
                        ) : (r.company || '—')}
                      </td>
                      <td style={{ ...cell, maxWidth: 180, wordBreak: 'break-word' }} title={r.counterparties || ''}>{r.key_contact || '—'}</td>
                      <td style={{ ...cell, maxWidth: 320 }}>{r.where_it_stands || r.sample_subject || '—'}</td>
                      <td style={{ ...cell, maxWidth: 240 }}>{r.suggested_action || '—'}</td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }} className="num">{r.days_quiet != null ? `${r.days_quiet}d ago` : '—'}</td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }} className="num" title={`${r.threads || 0} thread(s)`}>
                        {r.emails_out || 0}↑ {r.emails_in || 0}↓{r.meetings ? ` · ${r.meetings} mtg` : ''}
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        <span title={(r.evidence_note_ids || []).join('\n')} style={{ fontSize: 11.5, color: 'var(--text-3)', cursor: 'default' }}>
                          {(r.evidence_note_ids || []).length} notes
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

window.HandoffScanPanel = HandoffScanPanel;
